package floris.graphics.backoffice.pages.admin.projects.manage

import floris.graphics.extensions.isValidUrl
import floris.graphics.extensions.isYoutubeOrVimeoUrl
import floris.graphics.models.domain.models.Category
import floris.graphics.models.domain.models.Contributor
import floris.graphics.models.domain.models.MediaFile
import floris.graphics.models.domain.models.Techstack
import floris.graphics.models.domain.models.project.ProjectDetail
import floris.graphics.models.dto.requests.project.InsertProject
import floris.graphics.models.dto.requests.project.UpdateProject
import floris.graphics.repo.CategoryRepo
import floris.graphics.repo.ContributorRepo
import floris.graphics.repo.ProjectRepo
import floris.graphics.repo.TechstackRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers.Default
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.w3c.dom.url.URL
import org.w3c.files.File

class ManageProjectViewModel(
    private val projectId: String? = null,
    private val projectRepository: ProjectRepo = ProjectRepo,
    private val contributorRepository: ContributorRepo = ContributorRepo,
    private val categoryRepository: CategoryRepo = CategoryRepo,
    private val techstackRepository: TechstackRepo = TechstackRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Default)
) {

    private val _state = MutableStateFlow(ManageProjectState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<ManageProjectUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    fun onEvent(event: ManageProjectEvent) = coroutineScope.launch {
        when (event) {
            is ManageProjectEvent.LoadManageProject -> initializeProjectData()
            is ManageProjectEvent.Save -> saveProject()
            is ManageProjectEvent.Cancel -> _eventFlow.send(ManageProjectUiEvent.NavigateBack)
            is ManageProjectEvent.Delete -> deleteProject()
            is ManageProjectEvent.UpdateVideoUrl -> _state.update { it.copy(videoUrl = event.videoUrl) }
            is ManageProjectEvent.UpdateTitle -> _state.update { it.copy(title = event.title) }
            is ManageProjectEvent.UpdateSubtitle -> _state.update { it.copy(subtitle = event.subtitle) }
            is ManageProjectEvent.UpdateDescription -> _state.update { it.copy(description = event.description) }
            is ManageProjectEvent.UpdateAgency -> _state.update { it.copy(selectedAgency = event.contributor) }
            is ManageProjectEvent.UpdateAnimator -> _state.update { it.copy(selectedAnimator = event.contributor) }
            is ManageProjectEvent.UpdateClient -> _state.update { it.copy(selectedClient = event.contributor) }
            is ManageProjectEvent.UpdateDesigner -> _state.update { it.copy(selectedDesign = event.contributor) }
            is ManageProjectEvent.UpdateMultiCategory -> {
                val currentCategories = state.value.selectedCategories.toMutableList()
                if (event.category != null) {
                    if (currentCategories.any { it.id == event.category.id }) {
                        currentCategories.removeAll { it.id == event.category.id }
                    } else {
                        currentCategories.add(event.category)
                    }
                }
                _state.update { it.copy(selectedCategories = currentCategories) }
            }

            is ManageProjectEvent.UpdateMultiTechstack -> {
                val currentTechstacks = state.value.selectedTechstacks.toMutableList()
                if (event.techstack != null) {
                    if (currentTechstacks.any { it.id == event.techstack.id }) {
                        currentTechstacks.removeAll { it.id == event.techstack.id }
                    } else {
                        currentTechstacks.add(event.techstack)
                    }
                }
                _state.update { it.copy(selectedTechstacks = currentTechstacks) }
            }

            is ManageProjectEvent.UpdateThumbnail -> {
                if (state.value.isCreationFlow) {
                    // If we are creating a new project, we can just update the state with the thumbnail
                    _state.update {
                        it.copy(
                            thumbnailFile = event.thumbnail,
                            thumbnailUrl = event.thumbnail.let { URL.createObjectURL(it) }
                        )
                    }
                } else {
                    // If we are editing an existing project, we need to update the image remotely
                    updateFilesRemotely(thumbnail = event.thumbnail)
                }
            }

            is ManageProjectEvent.RemoveLogo -> {
                if (state.value.isCreationFlow) {
                    _state.update { it.copy(thumbnailFile = null, thumbnailUrl = null) }
                } else {
                    updateFilesRemotely(removeThumbnail = true)
                }
            }

            is ManageProjectEvent.UpdatePreviewVideo -> {
                if (state.value.isCreationFlow) {
                    // If we are creating a new project, we can just update the state with the preview video
                    _state.update {
                        it.copy(
                            previewVideoFile = event.previewVideoFile,
                            previewVideoUrl = event.previewVideoFile.let { URL.createObjectURL(it) }
                        )
                    }
                } else {
                    // If we are editing an existing project, we need to update the image remotely
                    updateFilesRemotely(previewVideo = event.previewVideoFile)
                }
            }

            is ManageProjectEvent.UpdateFrameFiles -> {
                if (state.value.isCreationFlow) {
                    val mediaFiles = event.frameFiles.map {
                        val string = URL.createObjectURL(it)
                        MediaFile(url = URL(string), mimeType = it.type)
                    }

                    // If we are creating a new project, we can just update the state with the frame file
                    _state.update { it.copy(frameFiles = it.frameFiles + event.frameFiles, frameMediaFiles = it.frameMediaFiles + mediaFiles) }
                } else {
                    // If we are editing an existing project, we need to update the image remotely
                    updateFilesRemotely(frameMedias = event.frameFiles)
                    // TODO [TicketNumber]: Update multiple frame media files at once
//                    updateFilesRemotely(previewVideo = event.frameFile)
                }
            }

            is ManageProjectEvent.RemoveMediaFile -> {
                // Remove the media file from the state
                val updatedFrameFiles = state.value.frameMediaFiles.filterNot { it.url == event.mediaFile.url }
                _state.update { it.copy(frameMediaFiles = updatedFrameFiles) }

                // If we are editing an existing project, we need to update the image remotely
                if (state.value.isEditFlow) {
                    projectRepository.deleteProjectMediaById(projectId ?: "", event.mediaFile.id)
                }
            }
        }
    }

    init {
        onEvent(ManageProjectEvent.LoadManageProject)
    }

    private suspend fun initializeProjectData() {
        val project = getProject()
        val categories = categoryRepository.getCategories()
        val contributors = contributorRepository.getContributors()
        val techstacks = techstackRepository.getTechstacks()

        _state.value = ManageProjectState(
            originalProject = project,
            categories = categories,
            techstacks = techstacks,
            contributors = contributors,
            videoUrl = project.videoUrl,
            previewVideoUrl = project.previewVideoMediaFile?.url?.toString(),
            thumbnailUrl = project.thumbnailMediaFile?.url?.toString(),
            frameMediaFiles = project.frameMediaFiles,
            title = project.title,
            subtitle = project.subtitle,
            description = project.content,
            selectedAgency = project.agency,
            selectedAnimator = project.animator,
            selectedClient = project.client,
            selectedDesign = project.design,
            selectedCategories = project.categories.orEmpty(),
            selectedTechstacks = project.techstacks.orEmpty()
        )
    }

    private suspend fun getProject(): ProjectDetail {
        if (projectId.isNullOrBlank()) return ProjectDetail()

        return projectRepository.getProject(projectId) ?: ProjectDetail()
    }

    private suspend fun saveProject() {
        val state = _state.value

        val result = if (projectId.isNullOrBlank()) {
            createProject(state)
        } else {
            updateProject(state)
        }

        if (result != null) {
            _eventFlow.send(ManageProjectUiEvent.NavigateBack)
        } else {
            _eventFlow.send(ManageProjectUiEvent.ShowError)
        }
    }

    private suspend fun createProject(state: ManageProjectState): ProjectDetail? {
        val project = InsertProject(
            title = state.title,
            subtitle = state.subtitle,
            content = state.description,
            categories = state.selectedCategories.map { it.id },
            techstacks = state.selectedTechstacks.map { it.id },
            clientId = state.selectedClient?.id,
            agencyId = state.selectedAgency?.id,
            designId = state.selectedDesign?.id,
            animatorId = state.selectedAnimator?.id,
            videoUrl = state.videoUrl
        )
        return if (project.isValid) {
            projectRepository.createProject(project, state.thumbnailFile, state.previewVideoFile, state.frameFiles)
        } else {
            null
        }
    }

    private suspend fun updateProject(state: ManageProjectState): ProjectDetail? {
        if (projectId.isNullOrBlank()) return null

        val project = UpdateProject(
            title = state.title,
            subtitle = state.subtitle,
            content = state.description,
            categories = state.selectedCategories.map { it.id },
            techstacks = state.selectedTechstacks.map { it.id },
            clientId = state.selectedClient?.id,
            agencyId = state.selectedAgency?.id,
            designId = state.selectedDesign?.id,
            animatorId = state.selectedAnimator?.id,
            videoUrl = state.videoUrl
        )

        return if (project.isValid) {
            projectRepository.updateProject(projectId, project)
        } else {
            null
        }
    }

    private suspend fun updateFilesRemotely(thumbnail: File? = null, removeThumbnail: Boolean = false, previewVideo: File? = null, frameMedias: List<File> = emptyList()) {
        if (projectId.isNullOrBlank()) return

        _state.update { it.copy(uploadingThumbnail = thumbnail != null, uploadingVideo = previewVideo != null) }
        val updatedProject = projectRepository.updateProjectImage(projectId, thumbnail, removeThumbnail, previewVideo, frameMedias)
        if (updatedProject != null) {
            _state.update {
                it.copy(
                    originalProject = updatedProject,
                    thumbnailFile = null,
                    thumbnailUrl = updatedProject.thumbnailMediaFile?.url?.toString(),
                    previewVideoFile = null,
                    previewVideoUrl = updatedProject.previewVideoMediaFile?.url?.toString(),
                    frameMediaFiles = updatedProject.frameMediaFiles,
                    uploadingThumbnail = false,
                    uploadingVideo = false
                )
            }
        } else {
            _state.update { it.copy(uploadingThumbnail = false, uploadingVideo = false) }
            _eventFlow.send(ManageProjectUiEvent.ShowError)
        }
    }

    private suspend fun deleteProject() {
        if (projectId.isNullOrBlank()) return

        projectRepository.deleteProject(projectId)
        _eventFlow.send(ManageProjectUiEvent.NavigateBack)
    }
}

data class ManageProjectState(
    val originalProject: ProjectDetail = ProjectDetail(),
    val contributors: List<Contributor> = emptyList(),
    val categories: List<Category> = emptyList(),
    val techstacks: List<Techstack> = emptyList(),
    val thumbnailFile: File? = null,
    val uploadingThumbnail: Boolean = false,
    val thumbnailUrl: String? = null,
    val previewVideoFile: File? = null,
    val uploadingVideo: Boolean = false,
    val previewVideoUrl: String? = null,
    val videoUrl: String = "",
    val frameFiles: List<File> = emptyList(),
    val frameMediaFiles: List<MediaFile> = emptyList(),
    val title: String = "",
    val subtitle: String = "",
    val description: String = "",
    val selectedCategories: List<Category> = emptyList(),
    val selectedTechstacks: List<Techstack> = emptyList(),
    val selectedClient: Contributor? = null,
    val selectedAgency: Contributor? = null,
    val selectedDesign: Contributor? = null,
    val selectedAnimator: Contributor? = null,
    val errorText: String = ""
) {
    private val titleChanged = title != originalProject.title
    private val thumbnailChanged = thumbnailFile != null
    private val previewVideoChanged = previewVideoFile != null
    private val videoUrlChanged = videoUrl != originalProject.videoUrl
    private val descriptionChanged = description != originalProject.content
    private val subtitleChanged = subtitle != originalProject.subtitle
    private val selectedCategoryChanged = selectedCategories.map { it.id } != originalProject.categories?.map { it.id }
    private val selectedTechstackChanged = selectedTechstacks.map { it.id } != originalProject.techstacks?.map { it.id }
    private val selectedClientChanged = selectedClient != originalProject.client
    private val selectedAgencyChanged = selectedAgency != originalProject.agency
    private val selectedDesignChanged = selectedDesign != originalProject.design
    private val selectedAnimatorChanged = selectedAnimator != originalProject.animator

    val isTitleValid: Boolean
        get() = title.isNotBlank()

    val isVideoUrlValid: Boolean
        get() = videoUrl.isNotBlank() && videoUrl.isValidUrl() && videoUrl.isYoutubeOrVimeoUrl()

    val isSubtitleValid: Boolean
        get() = subtitle.isNotBlank()

    val isDescriptionValid: Boolean
        get() = description.isNotBlank()

    val isCategoryValid: Boolean
        get() = selectedCategories.isNotEmpty()

    val isTechstackValid: Boolean
        get() = selectedTechstacks.isNotEmpty()

    val isCreationFlow: Boolean
        get() = originalProject.id.isBlank()

    val isEditFlow: Boolean
        get() = originalProject.id.isNotBlank()

    val hasChanged: Boolean
        get() = thumbnailChanged ||
                previewVideoChanged ||
                videoUrlChanged ||
                titleChanged ||
                descriptionChanged ||
                subtitleChanged ||
                selectedCategoryChanged ||
                selectedTechstackChanged ||
                selectedClientChanged ||
                selectedAgencyChanged ||
                selectedDesignChanged ||
                selectedAnimatorChanged
}

sealed class ManageProjectEvent {
    data object LoadManageProject : ManageProjectEvent()
    data class UpdateTitle(val title: String) : ManageProjectEvent()
    data class UpdateVideoUrl(val videoUrl: String) : ManageProjectEvent()
    data class UpdateSubtitle(val subtitle: String) : ManageProjectEvent()
    data class UpdateDescription(val description: String) : ManageProjectEvent()
    data class UpdateMultiCategory(val category: Category?) : ManageProjectEvent()
    data class UpdateMultiTechstack(val techstack: Techstack?) : ManageProjectEvent()
    data class UpdateClient(val contributor: Contributor?) : ManageProjectEvent()
    data class UpdateAgency(val contributor: Contributor?) : ManageProjectEvent()
    data class UpdateDesigner(val contributor: Contributor?) : ManageProjectEvent()
    data class UpdateAnimator(val contributor: Contributor?) : ManageProjectEvent()
    data class UpdateThumbnail(val thumbnail: File) : ManageProjectEvent()
    data class UpdatePreviewVideo(val previewVideoFile: File) : ManageProjectEvent()
    data class UpdateFrameFiles(val frameFiles: List<File>) : ManageProjectEvent()
    data class RemoveMediaFile(val mediaFile: MediaFile) : ManageProjectEvent()
    data object RemoveLogo : ManageProjectEvent()
    data object Save : ManageProjectEvent()
    data object Cancel : ManageProjectEvent()
    data object Delete : ManageProjectEvent()
}

sealed class ManageProjectUiEvent {
    data object ShowError : ManageProjectUiEvent()
    data object NavigateBack : ManageProjectUiEvent()
}

//fun String.base64ToFile(filename: String): File {
//    val parts = this.split(",")
//    val metadata = parts[0]
//    val base64Data = parts[1]
//
//    val mimeType = Regex("""data:(.*?);base64""").find(metadata)?.groupValues?.get(1) ?: "application/octet-stream"
//    val binaryString = window.atob(base64Data)
//    val byteArray = Uint8Array(binaryString.length)
//
//    for (i in binaryString.indices) {
//        val some = binaryString[i].code.toByte()
//        byteArray.set(index = i, value = some)
//    }
//
//    return File(arrayOf(byteArray), filename, FilePropertyBag(type = mimeType))
//}
